import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { notificationsAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Avatar } from "./Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { 
  LogOut, LayoutDashboard, User, Shield, Settings, ChevronDown, Bell, AlertTriangle, Info
} from "lucide-react";
import { BrandIcon } from "./BrandLogo";

export default function Header({ title, subtitle }) {
  const { trainer, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  useEffect(() => {
    if (trainer && !isAdmin) {
      loadUnreadCount();
    }
  }, [trainer, isAdmin]);

  const loadUnreadCount = async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  };

  const handleOpenNotifications = async () => {
    setShowNotifications(true);
    setLoadingNotifs(true);
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data.notifications || []);
      if (res.data.unread_count > 0) {
        await notificationsAPI.markAllRead();
        setUnreadCount(0);
      }
    } catch {} finally {
      setLoadingNotifs(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDashboard = () => {
    if (isAdmin) {
      navigate("/admin");
    } else {
      navigate("/dashboard");
    }
  };

  const handleProfile = () => {
    if (isAdmin) {
      navigate("/admin-profile");
    } else {
      navigate("/profile");
    }
  };

  const handleSettings = () => {
    if (isAdmin) {
      navigate("/admin-settings");
    } else {
      navigate("/settings");
    }
  };

  return (
    <>
    <header className="sticky top-0 z-40 glass border-b border-white/[0.06]">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left: Logo and Title */}
          <div className="flex items-center gap-3">
            <div 
              className="cursor-pointer transition-smooth hover:opacity-80"
              onClick={handleDashboard}
              data-testid="logo-dashboard-link"
            >
              <BrandIcon size="w-10 h-10" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 
                  className="font-heading text-base sm:text-lg font-extrabold tracking-widest uppercase cursor-pointer hover:opacity-80 transition-colors"
                  onClick={handleDashboard}
                >
                  <span className="text-white">FitTrack </span>
                  <span className="bg-gradient-to-r from-[#00E5FF] to-[#3B82F6] bg-clip-text text-transparent">COACH</span>
                </h1>
              </div>
              <p className="text-xs text-muted-foreground">
                {subtitle || trainer?.name}
              </p>
            </div>
          </div>

          {/* Right: Navigation Icons and User Menu */}
          <div className="flex items-center gap-2">
            {/* Beta Badge (for non-admin users) - Always show Beta during beta period */}
            {!isAdmin && (
              <Badge 
                className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs px-2 py-1"
                data-testid="plan-badge"
              >
                Beta
              </Badge>
            )}

            {/* Dashboard Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDashboard}
              className={`text-muted-foreground hover:text-foreground hover:bg-secondary/50 ${
                (location.pathname === '/dashboard' || location.pathname === '/admin') 
                  ? 'text-primary bg-primary/10' 
                  : ''
              }`}
              data-testid="nav-dashboard-btn"
              title="Dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
            </Button>

            {/* Admin Button (only for admins on non-admin pages) */}
            {isAdmin && location.pathname !== '/admin' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                className="text-primary hover:text-primary hover:bg-primary/10"
                data-testid="nav-admin-btn"
                title="Admin Panel"
              >
                <Shield className="w-5 h-5" />
              </Button>
            )}

            {/* Notification Bell (for non-admin coaches) */}
            {!isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenNotifications}
                className="relative text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                data-testid="notification-bell-btn"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
                    {unreadCount}
                  </span>
                )}
              </Button>
            )}

            {/* User Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-3 h-10"
                  data-testid="user-menu-btn"
                >
                  <Avatar src={trainer?.profile_photo} name={trainer?.name} size="sm" testId="navbar-avatar" />
                  <span className="hidden sm:block text-sm font-medium text-foreground max-w-[100px] truncate">
                    {trainer?.name?.split(' ')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 glass-card border-white/[0.06]">
                <DropdownMenuLabel className="font-heading uppercase tracking-wide text-xs text-muted-foreground">
                  My Account
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border/50" />
                <DropdownMenuItem 
                  className="cursor-pointer hover:bg-secondary/50"
                  onClick={handleProfile}
                  data-testid="menu-profile"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer hover:bg-secondary/50"
                  onClick={handleSettings}
                  data-testid="menu-settings"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/50" />
                <DropdownMenuItem 
                  className="cursor-pointer hover:bg-destructive/10 text-destructive focus:text-destructive"
                  onClick={handleLogout}
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

    </header>

      {/* Notifications Dialog */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="bg-card border-border max-w-md max-h-[70vh] overflow-y-auto mx-2 sm:mx-auto p-4 sm:p-6" data-testid="notifications-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Notifications
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {loadingNotifs ? (
              <div className="flex justify-center py-8"><div className="spinner" /></div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div key={notif.id} className={`p-3 rounded-xl border ${notif.type === 'warning' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-white/[0.02] border-white/[0.04]'}`} data-testid={`notification-${notif.id}`}>
                  <div className="flex items-start gap-2">
                    {notif.type === 'warning' ? (
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${notif.type === 'warning' ? 'text-amber-400' : 'text-primary'}`}>
                        {notif.type === 'warning' ? 'Admin Warning' : 'Notification'}
                      </p>
                      <p className="text-sm text-foreground">{notif.message}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {new Date(notif.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
